/**
 * Payments Analytics Dashboard Page
 * Displays payment metrics, trends, and past-due reports for landlords
 */

import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getPortfolioMetrics,
  getPaymentTrends,
  getPastDueReport,
  exportTransactions,
  listPortfolios,
  type PortfolioMetrics,
  type PaymentTrendData,
  type PastDueItem,
  type PortfolioWithStats,
  ApiClientError,
} from '../lib/api';
import { MetricCard } from '../components/payments/MetricCard';
import { PaymentTrendChart } from '../components/payments/PaymentTrendChart';
import { PastDueList } from '../components/payments/PastDueList';
import { ExportButton } from '../components/payments/ExportButton';
import { EmptyState } from '../components/layout/index';
import { Skeleton } from '../components/ui/skeleton';
import { HelpBlock, HelpTooltip } from '../components/ui/HelpTooltip';

export function PaymentsDashboardPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const portfolioId = searchParams.get('portfolioId');

  const [portfolios, setPortfolios] = useState<PortfolioWithStats[]>([]);
  const [metrics, setMetrics] = useState<PortfolioMetrics | null>(null);
  const [trends, setTrends] = useState<PaymentTrendData[]>([]);
  const [pastDue, setPastDue] = useState<PastDueItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Default date range: current month
  const [dateRange] = useState(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      startDate: start.toISOString(),
      endDate: end.toISOString(),
    };
  });

  // Load portfolios on mount
  useEffect(() => {
    loadPortfolios();
  }, []);

  // Load dashboard data when portfolio changes
  useEffect(() => {
    if (portfolioId) {
      loadDashboardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolioId, dateRange]);

  // Auto-select first portfolio if none selected
  useEffect(() => {
    if (portfolios.length > 0 && !portfolioId && portfolios[0]) {
      setSearchParams({ portfolioId: portfolios[0].id });
    }
  }, [portfolios, portfolioId, setSearchParams]);

  async function loadPortfolios() {
    try {
      const response = await listPortfolios();
      setPortfolios(response.portfolios);
    } catch (err) {
      console.error('Failed to load portfolios:', err);
      setError('Failed to load portfolios');
    }
  }

  async function loadDashboardData() {
    if (!portfolioId) return;

    try {
      setIsLoading(true);
      setError(null);

      const [metricsResponse, trendsResponse, pastDueResponse] = await Promise.all([
        getPortfolioMetrics(portfolioId, dateRange),
        getPaymentTrends(portfolioId, 6),
        getPastDueReport(portfolioId),
      ]);

      setMetrics(metricsResponse);
      setTrends(trendsResponse.trends);
      setPastDue(pastDueResponse.items);
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      if (err instanceof ApiClientError) {
        setError(err.message);
      } else {
        setError('Failed to load dashboard data');
      }
    } finally {
      setIsLoading(false);
    }
  }

  function handlePortfolioChange(newPortfolioId: string) {
    setSearchParams({ portfolioId: newPortfolioId });
  }

  async function handleExport() {
    if (!portfolioId) return;

    try {
      const response = await exportTransactions(portfolioId, dateRange);

      // Create a download link
      const blob = new Blob([response.csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `transactions-${dateRange.startDate.split('T')[0]}-to-${dateRange.endDate.split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export transactions:', err);
      setError('Failed to export transactions');
    }
  }

  if (portfolios.length === 0 && !isLoading) {
    return (
      <div className="max-w-4xl mx-auto pb-6 px-[var(--page-padding-x)]">
        <EmptyState
          title="No Portfolios Found"
          description="Create a portfolio to view payment analytics"
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto pb-6 px-[var(--page-padding-x)]">
        <div className="space-y-4">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-6 w-48" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
            <Skeleton className="h-28 rounded-lg" />
            <Skeleton className="h-28 rounded-lg" />
            <Skeleton className="h-28 rounded-lg" />
            <Skeleton className="h-28 rounded-lg" />
          </div>
          <Skeleton className="h-40 rounded-lg mt-4" />
          <Skeleton className="h-64 rounded-lg mt-4" />
        </div>
      </div>
    );
  }

  const portfolioSelector = portfolios.length > 1 ? (
    <select
      value={portfolioId || ''}
      onChange={(e) => handlePortfolioChange(e.target.value)}
      className="px-3 py-2 text-base font-medium text-foreground bg-background border border-input rounded-md cursor-pointer min-h-[44px]"
      aria-label="Select portfolio"
    >
      {portfolios.map((portfolio) => (
        <option key={portfolio.id} value={portfolio.id}>
          {portfolio.name}
        </option>
      ))}
    </select>
  ) : null;

  return (
    <div className="max-w-4xl mx-auto pb-6 px-[var(--page-padding-x)]">
      <div className="flex items-center justify-end pt-5 mb-4">
        <ExportButton onExport={handleExport} />
      </div>

      <div className="mb-4">
        <HelpBlock
          summary="Track rent collection across your portfolio. This dashboard shows payment metrics for the current month, a 6-month trend, and any accounts with outstanding balances."
          details={"What the metrics mean:\n\u2022 Collected \u2014 Total payments received this month (ACH and external combined).\n\u2022 Pending \u2014 Payments initiated but not yet settled (ACH transfers typically take 3\u20135 business days).\n\u2022 Past Due \u2014 Total unpaid balances beyond their due date and grace period.\n\u2022 Collection Rate \u2014 Percentage of charges that have been paid. A rate below 100% means some tenants have outstanding balances.\n\nIf you manage multiple portfolios, use the selector below to switch between them. The CSV export includes all transactions for the current month."}
        />
      </div>

      {portfolioSelector && (
        <div className="mb-4">
          {portfolioSelector}
        </div>
      )}

      {error && (
        <div className="bg-destructive/5 border border-destructive rounded-lg p-4 mb-6">
          <p className="text-base text-destructive m-0">{error}</p>
        </div>
      )}

      {/* Metrics Cards — 2-col mobile, 4-col desktop */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <MetricCard
          title="Collected"
          value={metrics?.totalCollected ?? 0}
          subtitle={`${metrics?.leaseCount ?? 0} leases`}
          color="success"
        />
        <MetricCard
          title="Pending"
          value={metrics?.totalPending ?? 0}
          subtitle="In progress"
          color="warning"
        />
        <MetricCard
          title="Past Due"
          value={metrics?.totalPastDue ?? 0}
          subtitle={`${pastDue.length} accounts`}
          color="error"
        />
        {/* 4th slot — collection rate summary */}
        {metrics && (
          <div className="rounded-lg p-5 border border-border bg-card border-l-4 border-l-primary">
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide m-0">
              Collection Rate
            </h3>
            <div className="text-3xl font-bold text-primary mt-2">
              {metrics.collectionRate.toFixed(1)}%
            </div>
            <p className="text-sm text-muted-foreground mt-1 m-0">of charges collected</p>
          </div>
        )}
      </div>

      {/* Collection Rate Progress Bar */}
      {metrics && (
        <div className="mb-8">
          <div className="rounded-lg border border-border bg-card p-5">
            <h3 className="text-lg font-semibold m-0">
              Collection Rate
            </h3>
            <div className="mt-4 flex items-baseline gap-2">
              <span className="text-3xl font-bold text-primary">
                {metrics.collectionRate.toFixed(1)}%
              </span>
              <span className="text-base text-muted-foreground">
                of charges collected
              </span>
            </div>
            <div className="mt-3">
              <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-500 ease-out"
                  style={{ width: `${Math.min(metrics.collectionRate, 100)}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Trend Chart */}
      <div className="mb-8">
        <div className="rounded-lg border border-border bg-card p-6">
          <h3 className="text-xl font-semibold m-0">
            Payment Trends (6 Months)
          </h3>
          <div className="mt-6">
            <PaymentTrendChart data={trends} />
          </div>
        </div>
      </div>

      {/* Past Due Accounts */}
      <div>
        <div className="rounded-lg border border-border bg-card p-6">
          <h3 className="text-xl font-semibold m-0 inline-flex items-center gap-1.5">
            Past Due Accounts ({pastDue.length})
            <HelpTooltip text="Accounts where the tenant's balance is unpaid past the due date plus grace period. For ACH leases, late fees are assessed automatically. For external payment leases, you'll need to approve late fees on the External Payments page." />
          </h3>
          <div className="mt-6">
            <PastDueList items={pastDue} />
          </div>
        </div>
      </div>
    </div>
  );
}
